"use client";

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";

import { bindGtmUser } from "@/shared/analytics/events";
import {
  initMixpanel,
  mixpanelIdentify,
  mixpanelReset,
  mixpanelTrackPageview,
} from "@/shared/analytics/mixpanel";
import { useAuth } from "@/shared/auth/hooks";

function resolvePageName(pathname: string | null): string | undefined {
  if (!pathname) return undefined;
  if (pathname === "/") return "Main Page";
  if (pathname.startsWith("/shopping") || pathname.startsWith("/shop")) return "Category View";
  if (pathname.startsWith("/product")) return "Product View";
  if (pathname.startsWith("/search")) return "Search View";
  if (pathname.startsWith("/wishlist") || pathname.startsWith("/favorite")) return "Favorite View";
  if (pathname.startsWith("/brands") || pathname.startsWith("/brand")) return "Brand View";
  if (pathname.startsWith("/cart")) return "Cart View";
  if (pathname.startsWith("/payment/payment-success")) return "Order Complete View";
  if (pathname.startsWith("/checkout") || pathname.startsWith("/payment")) return "Payment View";
  if (pathname.startsWith("/orders")) {
    if (pathname.includes("/cancel")) return "Cancel View";
    if (pathname.includes("/refund")) return "Order Detail View";
    if (pathname.includes("/return")) return "Return/Exchange View";
    return "Order Detail View";
  }
  if (pathname.startsWith("/mypage")) return "Mypage View";
  if (pathname.startsWith("/faq")) return "FAQ View";
  if (pathname.startsWith("/claim")) return "Claim View";
  if (pathname.startsWith("/apply")) return "FAQ Brand View";
  if (pathname.startsWith("/login")) return "LogIn View";
  if (pathname.startsWith("/signup") || pathname.startsWith("/register")) return "Register View";
  if (pathname.startsWith("/flash-sale")) return "Flash Sale View";
  if (pathname.startsWith("/promotions")) return "Promotion View";
  return undefined;
}

export function MixpanelProvider() {
  const pathname = usePathname();
  const { user, isLoading } = useAuth();
  const lastIdentifiedRef = useRef<string | null>(null);
  const readyRef = useRef(false);

  const isAdminPath = pathname?.startsWith("/admin") ?? false;

  useEffect(() => {
    if (isAdminPath) return;
    readyRef.current = initMixpanel();
  }, [isAdminPath]);

  useEffect(() => {
    if (isAdminPath || !readyRef.current) return;
    if (!pathname) return;
    const pageName = resolvePageName(pathname);
    if (pageName) {
      mixpanelTrackPageview(pageName);
    } else {
      mixpanelTrackPageview(pathname);
    }
  }, [pathname, isAdminPath]);

  useEffect(() => {
    if (isAdminPath || !readyRef.current) return;
    if (isLoading) return;

    if (user?.id) {
      if (lastIdentifiedRef.current !== user.id) {
        mixpanelIdentify(user.id);
        // 레거시 `_app.js:119` 가 로그인 성공 시 user_id 를 dataLayer 에 바인딩한
        // 것과 동일 — GTM 컨테이너의 후속 이벤트가 이 값을 기준으로 사용자 매핑.
        bindGtmUser(user.id);
        lastIdentifiedRef.current = user.id;
      }
    } else if (lastIdentifiedRef.current) {
      mixpanelReset();
      lastIdentifiedRef.current = null;
    }
  }, [user, isLoading, isAdminPath]);

  return null;
}
